import { useLanguage } from "@/hooks/useLanguage";
import { useMode } from "@/lib/mode";
import { useZeroObstruction } from "@/lib/pr22/zeroObstruction";
import { useXrayLayoutSettings } from "@/lib/pr27/tacticalXrayLayout";

interface Props {
  goalPct: number;
  keyPassPct: number;
  defenderRiskHigh: boolean;
  visible: boolean;
}

export function PredictiveVector({ goalPct, keyPassPct, defenderRiskHigh, visible }: Props) {
  const { t, lang } = useLanguage();
  const [mode] = useMode();
  const { cleared } = useZeroObstruction();
  const xraySettings = useXrayLayoutSettings();
  if (!visible) return null;
  // PR-27: only render the right-side X-Ray card when layout explicitly opts
  // into over_field. Default (below_radar/top_band/side_panel) suppresses the
  // overlay to keep the pitch fully visible — the new CompactTacticalXrayCard
  // renders elsewhere.
  const showSideCard = xraySettings.layout === "over_field" && !cleared;

  const xrayLabel = lang === "EN" ? "TACTICAL X-RAY" : lang === "PT" ? "RAIO-X TÁTICO" : "RAYOS X TÁCTICO";
  const goalPctInt = Math.round(goalPct * 100);
  // Readability-first: solid near-black panel with white text. Warm hue is
  // only a small accent, never a text color or background behind the copy.
  const accent =
    goalPct > 0.66 ? "oklch(0.7 0.25 27)" : goalPct > 0.4 ? "oklch(0.82 0.2 60)" : "oklch(0.9 0.18 95)";

  return (
    <>
      {/* Floating goal % chip — simple, solid, high contrast */}
      <div
        className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 flex max-w-[calc(100%-1rem)] flex-col items-center justify-center gap-0.5 rounded-lg border px-3 py-2 text-center font-black uppercase leading-tight shadow-lg sm:flex-row sm:gap-1.5 sm:rounded-full sm:px-4 sm:py-2"
        style={{
          background: "oklch(0.06 0.005 260)",
          color: "oklch(1 0 0)",
          border: `1.5px solid ${accent}`,
          letterSpacing: 0,
        }}
        role="status"
        aria-live="polite"
      >
        <span className="flex items-center justify-center gap-1.5 text-[13px] text-[oklch(1_0_0)] sm:text-[14px]">
          <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
          <span>{t("goal_prob").toUpperCase()}</span>
        </span>
        <span className="tabular-nums text-[18px] leading-none text-[oklch(1_0_0)] sm:text-[14px]">{goalPctInt}%</span>
      </div>
      {showSideCard && <div
      className="pointer-events-none absolute right-3 top-12 z-10 max-w-[200px] rounded-xl border border-[oklch(0.82_0.24_145)]/50 bg-background/85 px-3 py-2 text-[10px] font-bold backdrop-blur-md shadow-2xl animate-fade-in"
      role="status"
      aria-live="polite"
    >
      <div className="mb-1 flex items-center gap-1.5 text-[9px] tracking-widest text-[oklch(0.82_0.24_145)]">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[oklch(0.82_0.24_145)]" />
        {xrayLabel}
      </div>
      <div className="space-y-1">
        <Row label={t("goal_prob")} pct={goalPct} color="oklch(0.82 0.24 145)" />
        <Row label={t("key_pass")} pct={keyPassPct} color="oklch(0.7 0.18 230)" />
        {defenderRiskHigh && (
          <div className="mt-1 rounded bg-primary/20 px-1.5 py-0.5 text-[9px] text-primary">
            {t("def_risk")}
          </div>
        )}
        {mode === "betting" && goalPct > 0.5 && (
          <div className="mt-1 rounded bg-[oklch(0.82_0.24_145)]/20 px-1.5 py-0.5 text-[9px] text-[oklch(0.82_0.24_145)]">
            ⚡ Quick action +{(goalPct * 4.5).toFixed(2)}
          </div>
        )}
      </div>
      </div>}
    </>
  );
}

function Row({ label, pct, color }: { label: string; pct: number; color: string }) {
  const value = Math.round(pct * 100);
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-foreground/85">{label}</span>
        <span className="tabular-nums" style={{ color }}>{value}%</span>
      </div>
      <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-secondary/50">
        <div className="h-full rounded-full" style={{ width: `${value}%`, background: color, transition: "width 300ms ease" }} />
      </div>
    </div>
  );
}